import React from "react";

import { Container, Grid } from "@mui/material";

import CustomInput from "../../../../helper-components/input/CustomInput";
import CustomStepper from "../../../../helper-components/form/CustomStepper";
import ConfirmationDialog from "../../../../helper-components/input/ConfirmationDialog";
import TransparentFragment from "../../../../helper-components/misc/TransparentFragment";

import CustomSelection from "../../../../helper-components/form/CustomSelection";

import { useDialog } from "../../../../helper-functions/hooks/useDialog";
import { querySelectResources } from "../../../../helper-functions/api/resourceQueries";
import { addFormToObj, getEllipsis, setInsertObjectState, setValue } from "../../../../helper-functions/shared/miscUtils";
import { formHasValue, validateRow } from "../../../../helper-functions/validation/objectValidation";

import { TEXT_TRANSLATION_VALIDATION } from "../../../../helper-functions/validation/translationValidation";
import { TEXT_TRANSLATION_OBJECT, TRANSLATE_LANGUAGES } from "../../../../config/constants/Constants";

/**
 * A stepper component to add a new resource.
 * Includes helper descriptions and validation.
 */
export default function TextTranslationAddStepper(props) {
    const { enqueueDialog, setOpen, ...options } = useDialog(); // Nested dialog component.

    // Because modals don't re-render if the props' state changes, use a callback.
    const { callback, row: existingRow = {} } = props;

    const object = TEXT_TRANSLATION_OBJECT;
    const validation = TEXT_TRANSLATION_VALIDATION;

    const [row, setRow] = React.useState(addFormToObj(JSON.parse(JSON.stringify(object))));

    // Initialize the temporary row object with existing values.
    React.useEffect(() => {
        const newRow = {
            ...row,
            ...existingRow,

            _instructions: existingRow.meta_data?.instructions ?? "",

            form: {
                ...row.form,
                ...existingRow.form,
                saved: false // Required to allow editing input fields.
            }
        };

        setInsertObjectState(setRow, newRow);
    }, []);

    /**
     * Executed when typing something into an input field.
     */
    const onChange = (obj, key, value) => {
        const newRow = JSON.parse(JSON.stringify(row));
        setValue(newRow, key, value);

        if (key === "_instructions") {
            if (!newRow.meta_data) newRow.meta_data = {};
            newRow.meta_data.instructions = value;
        }

        validateRow(obj, validation, newRow);
        setInsertObjectState(setRow, newRow);
    };

    const handleClick = (confirm) => {
        const options = { row };

        callback(confirm, options);
    };

    const handleResource = (selected = []) => {
        const ids = selected.map((el) => el.id);
        onChange(row, "resource_id", ids?.[0]);
    };

    // Create the step components.
    const step1 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Select the resource to translate.`} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomSelection
                            {...props}
                            callback={handleResource}
                            query={querySelectResources}
                            getPrimarySearchText={(el) => el?.name}
                            getSecondarySearchText={(el) => el?.description}
                            getPrimaryText={(el) => getEllipsis(el?.name, 50)}
                            getSecondaryText={(el) => getEllipsis(el?.description, 50)}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step2 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment value={`Define the language the resource transcript is to be translated into.`} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput name="language" row={row} onChange={onChange} label="Translate into language" select={TRANSLATE_LANGUAGES} />
                    </Grid>

                    <Grid item xs={12}>
                        <CustomInput
                            name="_instructions"
                            row={row}
                            onChange={onChange}
                            label="Additional instructions"
                            multiline
                            minRows={2}
                            maxRows={4}
                            placeholder="Remove sponsored segments and recommended resources"
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    const step3 = () => {
        return (
            <Container style={{ margin: 0, padding: 0 }}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <TransparentFragment
                            value={`Creating new text translation with the following details:
                            Resource ID: ${row?.resource_id || "N/A"}
                            Language: ${row.language || "N/A"}

                            Click "Apply" to save the request or "Reset" to start over.`}
                        />
                    </Grid>
                </Grid>
            </Container>
        );
    };

    // Define the steps and the final step.
    const steps = [
        {
            label: "Select resource",
            optional: false,
            component: step1(),
            nextDisabled: !formHasValue(row, "resource_id")
        },
        {
            label: "Choose language",
            optional: false,
            component: step2(),
            nextDisabled: !formHasValue(row, "language")
        }
    ];

    const final = { component: step3() };

    return (
        <>
            <ConfirmationDialog
                {...props}
                options={{
                    enqueueDialog,
                    setOpen,
                    ...options
                }}
            />

            <CustomStepper steps={steps} final={final} callback={(confirm) => handleClick(confirm)} />
        </>
    );
}
